// Nirvana appearance engine — token/gradient helpers and DOM application.
// Themes are stored as "R G B" triplet token maps (light + dark), plus a
// gradient set, typography, radius, shadows and density.

export const TOKEN_KEYS = [
  "background", "foreground", "card", "card-foreground", "popover", "popover-foreground",
  "primary", "primary-foreground", "secondary", "secondary-foreground",
  "muted", "muted-foreground", "accent", "accent-foreground", "accent-subtle",
  "destructive", "destructive-foreground", "border", "input", "ring",
  "sidebar-bg", "sidebar-fg", "sidebar-border", "sidebar-accent", "sidebar-active",
];

// Editable swatches surfaced in the theme editor (grouped, friendly labels).
export const SWATCH_GROUPS = [
  {
    label: "Core", swatches: [
      { key: "primary", label: "Primary" },
      { key: "accent", label: "Gold accent" },
      { key: "background", label: "Background" },
      { key: "foreground", label: "Text" },
      { key: "secondary", label: "Sage surface" },
      { key: "destructive", label: "Error" },
    ],
  },
  {
    label: "Surfaces", swatches: [
      { key: "card", label: "Card" },
      { key: "muted", label: "Muted" },
      { key: "border", label: "Border" },
      { key: "ring", label: "Focus ring" },
    ],
  },
  {
    label: "Sidebar", swatches: [
      { key: "sidebar-bg", label: "Sidebar" },
      { key: "sidebar-fg", label: "Sidebar text" },
      { key: "sidebar-active", label: "Sidebar active" },
    ],
  },
];

export const GRADIENT_KEYS = [
  { key: "primary", label: "Primary Gradient", desc: "Accent surfaces & highlights." },
  { key: "hero", label: "Hero Gradient", desc: "Dashboard hero banner." },
  { key: "login", label: "Login Gradient", desc: "Sign-in experience." },
  { key: "sidebar", label: "Sidebar Gradient", desc: "Navigation rail." },
  { key: "card", label: "Card Highlight", desc: "Featured card wash." },
  { key: "dark", label: "Dark Mode Gradient", desc: "Deep dark surfaces." },
];

export const FONT_CHOICES = {
  heading: ["Cormorant Garamond", "Georgia", "Plus Jakarta Sans", "system-ui"],
  body: ["Plus Jakarta Sans", "Inter", "system-ui", "Georgia"],
  mono: ["JetBrains Mono", "monospace"],
};

export const DENSITIES = ["compact", "comfortable", "spacious"];
export const SHADOWS = ["flat", "soft", "elevated"];
export const RADII = [
  { label: "Sharp", value: "0.25rem" },
  { label: "Soft", value: "0.5rem" },
  { label: "Rounded", value: "0.75rem" },
  { label: "Pill", value: "1rem" },
];

// ---- colour conversions ----
export function tripletToHex(triplet) {
  const parts = (triplet || "").trim().split(/\s+/).map(Number);
  if (parts.length < 3 || parts.some(Number.isNaN)) return "#1c3d2d";
  return "#" + parts.slice(0, 3).map((n) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, "0")).join("");
}
export function hexToTriplet(hex) {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return "0 0 0";
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)).join(" ");
}
function hexToRgba(hex, alpha) {
  const h = (hex || "#000000").replace("#", "");
  const n = h.length === 6 ? h : "000000";
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(n.slice(i, i + 2), 16));
  return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, alpha))})`;
}

// Build a CSS gradient string from a gradient config.
export function buildGradientCss(g) {
  if (!g) return "none";
  const angle = g.angle ?? 135;
  const opacity = g.opacity ?? 1;
  const intensity = g.intensity ?? 0.7;
  const alpha = Math.max(0, Math.min(1, opacity * (0.4 + intensity * 0.6)));
  const stops = [g.from, g.mid, g.to].filter(Boolean).map((c) => hexToRgba(c, alpha));
  if (stops.length < 2) return "none";
  return `linear-gradient(${angle}deg, ${stops.join(", ")})`;
}

// ---- DOM application ----
// Injects a <style> element carrying the theme's light + dark tokens so both
// modes work without per-token JS toggling. Overrides are applied on top.
function tokensToCss(map) {
  return TOKEN_KEYS.filter((k) => map && map[k])
    .map((k) => `--${k}: ${map[k]};`).join(" ");
}

export function applyTheme(config, { overrides = {} } = {}) {
  if (!config) return;
  const root = document.documentElement;
  const light = { ...(config.light || {}) };
  const dark = { ...(config.dark || {}) };
  // personal overrides (flat token->triplet) applied to both modes
  Object.entries(overrides || {}).forEach(([k, v]) => {
    const key = k.replace(/^--/, "");
    light[key] = v; dark[key] = v;
  });

  const css = `:root{${tokensToCss(light)} --radius:${config.radius || "0.5rem"};`
    + `--font-heading:${JSON.stringify(config.typography?.heading || "Cormorant Garamond")};`
    + `--font-body:${JSON.stringify(config.typography?.body || "Plus Jakarta Sans")};`
    + `--font-mono:${JSON.stringify(config.typography?.mono || "JetBrains Mono")};}`
    + `.dark{${tokensToCss(dark)} --radius:${config.radius || "0.5rem"};}`;

  let el = document.getElementById("nirvana-theme-style");
  if (!el) {
    el = document.createElement("style");
    el.id = "nirvana-theme-style";
    document.head.appendChild(el);
  }
  el.textContent = css;

  // gradients
  const g = config.gradients || {};
  GRADIENT_KEYS.forEach(({ key }) => {
    root.style.setProperty(`--gradient-${key}`, buildGradientCss(g[key]));
  });

  // typography scale
  const scale = config.typography?.scale || 1;
  root.style.setProperty("--font-scale", String(scale));
  root.style.fontSize = `${16 * scale}px`;

  // density + shadows classes
  root.classList.remove("density-compact", "density-comfortable", "density-spacious");
  root.classList.add(`density-${config.density || "comfortable"}`);
  root.classList.remove("shadows-flat", "shadows-soft", "shadows-elevated");
  root.classList.add(`shadows-${config.shadows || "soft"}`);
}

export function applyMode(mode) {
  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const isDark = mode === "dark" || (mode === "system" && prefersDark);
  document.documentElement.classList.toggle("dark", isDark);
  return isDark ? "dark" : "light";
}

export function applyReducedMotion(on) {
  document.documentElement.classList.toggle("reduce-motion", !!on);
}

// Fallback Nirvana Green config so pre-auth pages (login) are themed.
export const FALLBACK_CONFIG = {
  schema: "nirvana-theme@1",
  typography: { heading: "Cormorant Garamond", body: "Plus Jakarta Sans", mono: "JetBrains Mono", scale: 1.0 },
  radius: "0.5rem", shadows: "soft", density: "comfortable",
  light: {
    background: "250 248 245", foreground: "26 46 35", card: "255 255 255", "card-foreground": "26 46 35",
    popover: "255 255 255", "popover-foreground": "26 46 35", primary: "28 61 45", "primary-foreground": "250 248 245",
    secondary: "228 237 231", "secondary-foreground": "28 61 45", muted: "239 243 239", "muted-foreground": "80 99 86",
    accent: "197 160 89", "accent-foreground": "26 46 35", "accent-subtle": "250 244 232",
    destructive: "153 46 46", "destructive-foreground": "255 255 255", border: "216 227 218", input: "226 235 228",
    ring: "45 90 67", "sidebar-bg": "20 45 33", "sidebar-fg": "232 239 234", "sidebar-border": "31 66 49",
    "sidebar-accent": "35 73 54", "sidebar-active": "197 160 89",
  },
  dark: {
    background: "15 26 20", foreground: "234 240 236", card: "22 38 30", "card-foreground": "234 240 236",
    popover: "22 38 30", "popover-foreground": "234 240 236", primary: "61 122 92", "primary-foreground": "250 248 245",
    secondary: "32 54 43", "secondary-foreground": "212 229 220", muted: "27 44 35", "muted-foreground": "138 160 146",
    accent: "212 175 55", "accent-foreground": "15 26 20", "accent-subtle": "40 35 19",
    destructive: "184 66 66", "destructive-foreground": "255 255 255", border: "39 61 49", input: "39 61 49",
    ring: "78 147 112", "sidebar-bg": "11 20 15", "sidebar-fg": "221 240 229", "sidebar-border": "23 42 32",
    "sidebar-accent": "26 51 38", "sidebar-active": "212 175 55",
  },
  gradients: {
    primary: { from: "#142d21", to: "#1c3d2d", angle: 135, opacity: 1, intensity: 0.7 },
    hero: { from: "#142d21", mid: "#1c3d2d", to: "#0f1a14", angle: 160, opacity: 1, intensity: 0.55 },
    login: { from: "#142d21", to: "#0f1a14", angle: 145, opacity: 1, intensity: 0.85 },
    sidebar: { from: "#142d21", to: "#0f1a14", angle: 180, opacity: 1, intensity: 0.9 },
    card: { from: "#c5a059", to: "#ffffff", angle: 120, opacity: 0.1, intensity: 0.35 },
    dark: { from: "#0f1a14", mid: "#142d21", to: "#0b140f", angle: 160, opacity: 1, intensity: 0.85 },
  },
};

// ---- typography engine (AP-1) ----
export const FONT_CATALOGUE = {
  brand: ["Cormorant Garamond", "Spectral", "Playfair Display", "Manrope", "Inter"],
  heading: ["Cormorant Garamond", "Spectral", "Playfair Display", "Manrope", "Inter"],
  body: ["Plus Jakarta Sans", "Inter", "Spectral", "Work Sans", "Manrope"],
  ui: ["Plus Jakarta Sans", "Inter", "Manrope", "Work Sans"],
  mono: ["JetBrains Mono", "IBM Plex Mono"],
  devanagari: ["Noto Serif Devanagari", "Noto Sans Devanagari"],
};
const GOOGLE_FAMILIES = [
  "Cormorant+Garamond:wght@400;500;600;700", "Plus+Jakarta+Sans:wght@300;400;500;600;700",
  "JetBrains+Mono:wght@400;500", "Spectral:wght@400;500;600", "Inter:wght@400;500;600;700",
  "Manrope:wght@400;500;600;700", "Playfair+Display:wght@500;600;700", "Work+Sans:wght@400;500;600",
  "IBM+Plex+Mono:wght@400;500", "Noto+Serif+Devanagari:wght@400;500;600", "Noto+Sans+Devanagari:wght@400;500;600",
];
export function ensureFontsLoaded() {
  if (document.getElementById("nirvana-google-fonts")) return;
  const link = document.createElement("link");
  link.id = "nirvana-google-fonts"; link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?" + GOOGLE_FAMILIES.map((f) => `family=${f}`).join("&") + "&display=swap";
  document.head.appendChild(link);
}
export function applyTypography(typ, customFonts = []) {
  if (!typ) return;
  ensureFontsLoaded();
  const fmt = (f) => (f === "ttf" ? "truetype" : f === "otf" ? "opentype" : f);
  const faces = (customFonts || []).filter((f) => f.url).map((f) =>
    `@font-face{font-family:${JSON.stringify(f.family)};src:url(${JSON.stringify(f.url)}) format(${JSON.stringify(fmt(f.format))});font-weight:${f.weight || "400"};font-style:${f.style || "normal"};font-display:swap;}`).join("");
  let fe = document.getElementById("nirvana-font-faces");
  if (!fe) { fe = document.createElement("style"); fe.id = "nirvana-font-faces"; document.head.appendChild(fe); }
  fe.textContent = faces;

  const roles = typ.roles || {}; const c = typ.controls || {};
  const dv = JSON.stringify(roles.devanagari || "Noto Serif Devanagari");
  const stack = (fam, fb) => `${JSON.stringify(fam)}, ${dv}, ${fb}`;
  const r = document.documentElement.style;
  r.setProperty("--font-brand", stack(roles.brand || "Cormorant Garamond", "Georgia, serif"));
  r.setProperty("--font-heading", stack(roles.heading || "Cormorant Garamond", "Georgia, serif"));
  r.setProperty("--font-body", stack(roles.body || "Plus Jakarta Sans", "sans-serif"));
  r.setProperty("--font-ui", stack(roles.ui || "Plus Jakarta Sans", "sans-serif"));
  r.setProperty("--font-mono", `${JSON.stringify(roles.mono || "JetBrains Mono")}, monospace`);
  r.setProperty("--font-deva", `${dv}, serif`);
  r.setProperty("--heading-scale", String(c.heading_scale ?? 1));
  r.setProperty("--line-height", String(c.line_height ?? 1.6));
  r.setProperty("--letter-spacing", `${c.letter_spacing ?? 0}px`);
  r.setProperty("--deva-line-height", String(c.deva_line_height ?? 1.7));
  document.documentElement.style.fontSize = `${(c.base_size ?? 16) * (c.ui_scale ?? 1)}px`;
}

export function applyLayout(layout) {
  if (!layout) return;
  const t = layout.tokens || {};
  const r = document.documentElement.style;
  const setpx = (k, v) => { if (v != null) r.setProperty(k, `${v}px`); };
  setpx("--page-px", t.page_px); setpx("--page-py", t.page_py);
  setpx("--card-pad", t.card_pad); setpx("--section-gap", t.section_gap);
  setpx("--content-max", t.content_max); setpx("--header-h", t.header_h);
  setpx("--sidebar-w", t.sidebar_w); setpx("--sidebar-compact-w", t.sidebar_compact_w);
  setpx("--radius", t.radius);
  if (t.shadow != null) r.setProperty("--shadow-strength", String(t.shadow));
  document.documentElement.dataset.density = t.density || "comfortable";
}

// ---- atmosphere engine (AP-3): ambient glow + motif placement ----
export const GLOW_STYLES = [
  { value: "radial_mist", label: "Radial Mist", desc: "Soft radial haze in the corners." },
  { value: "halo", label: "Halo", desc: "A gentle luminous ring behind content." },
  { value: "soft_gradient", label: "Soft Gradient", desc: "A calm directional wash." },
  { value: "none", label: "None", desc: "No ambient glow." },
];
export const GLOW_INTENSITIES = [
  { value: "subtle", label: "Subtle", mult: 1 },
  { value: "balanced", label: "Balanced", mult: 1.9 },
  { value: "vivid", label: "Vivid", mult: 3.2 },
];
export const MOTIF_PACKS = ["shiva", "lalita", "vishnu", "universal", "devotional", "custom"];
export const PLACEMENT_SLOTS = [
  { id: "login_bg", name: "Login Background" },
  { id: "dashboard_hero", name: "Home / Dashboard Hero" },
  { id: "workspace_header", name: "Workspace Overview Header" },
  { id: "campaign_hero", name: "Campaign / Festival Hero" },
  { id: "empty_state", name: "Empty States" },
  { id: "page_edge", name: "Page Edge / Corner" },
  { id: "sidebar_footer", name: "Sidebar Footer" },
  { id: "section_hero", name: "Section / Hero Background" },
];
export const ATMOSPHERE_SURFACES = [
  { id: "login", name: "Login" },
  { id: "dashboard_hero", name: "Home / Dashboard" },
  { id: "workspace_header", name: "Workspace overview" },
  { id: "campaign_hero", name: "Campaign / Festival" },
  { id: "empty_state", name: "Empty states" },
  { id: "section_hero", name: "Section hero" },
  { id: "page_edge", name: "General page edge" },
  { id: "sidebar_footer", name: "Sidebar footer" },
  { id: "dense", name: "Dense operational screens" },
];

// Map an app route to its atmosphere surface (for the global shell layer).
export function surfaceFromPath(pathname = "/") {
  const p = pathname || "/";
  if (p === "/" || p === "/dashboard") return "dashboard_hero";
  if (/^\/(settings|team\/activity|audit)/.test(p)) return "dense";
  if (/^\/(campaigns|festival)/.test(p)) return "campaign_hero";
  if (/^\/(blogs|instagram|youtube|knowledge|performance|reports)/.test(p)) return "workspace_header";
  return "page_edge";
}

// Position → CSS coordinates for a decorative layer.
const POS = {
  "top-right": { top: "-6%", right: "-4%" }, "top-left": { top: "-6%", left: "-4%" },
  "bottom-right": { bottom: "-6%", right: "-4%" }, "bottom-left": { bottom: "-6%", left: "-4%" },
  "center": { top: "50%", left: "50%", transform: "translate(-50%,-50%)" },
  "top": { top: "-8%", left: "50%", transform: "translateX(-50%)" },
};

// Build the ambient glow background-image for a resolved atmosphere + surface.
// Uses theme tokens (rgb(var(--…))) so it adapts to every theme + light/dark,
// unless a custom palette is chosen. Returns "" when the surface glow is off.
export function glowBackground(atmosphere, { surface = "page_edge", mode = "light" } = {}) {
  if (!atmosphere) return "";
  const glow = atmosphere.glow || {};
  if (glow.style === "none") return "";
  const vis = (atmosphere.surfaces || {})[surface] || "off";
  if (vis === "off") return "";
  const iMult = (GLOW_INTENSITIES.find((i) => i.value === glow.intensity) || GLOW_INTENSITIES[0]).mult;
  const modeCfg = (mode === "dark" ? glow.dark : glow.light) || { enabled: true, opacity: 1 };
  if (modeCfg.enabled === false) return "";
  const surfMult = vis === "reduced" ? 0.5 : 1;
  const baseOp = (mode === "dark" ? 0.05 : 0.035) * iMult * surfMult * (modeCfg.opacity ?? 1);
  const auto = glow.palette !== "custom";
  const a = auto ? "var(--accent)" : null;
  const b = auto ? "var(--primary)" : null;
  const cA = auto ? `rgb(${a} / ${baseOp.toFixed(3)})` : hexToRgba(glow.custom?.a || "#c5a059", baseOp);
  const cB = auto ? `rgb(${b} / ${(baseOp * 0.8).toFixed(3)})` : hexToRgba(glow.custom?.b || "#1c3d2d", baseOp * 0.8);
  if (glow.style === "halo") {
    return `radial-gradient(60% 55% at 50% 18%, ${cA}, transparent 60%), radial-gradient(50% 50% at 50% 90%, ${cB}, transparent 65%)`;
  }
  if (glow.style === "soft_gradient") {
    return `linear-gradient(155deg, ${cA} 0%, transparent 45%), linear-gradient(320deg, ${cB} 0%, transparent 55%)`;
  }
  // radial_mist (default)
  return `radial-gradient(45% 45% at 12% 0%, ${cA}, transparent 70%), radial-gradient(42% 50% at 100% 30%, ${cB}, transparent 72%)`;
}

// Resolve the motif to render in a slot (returns null if nothing to draw).
export function resolveMotif(atmosphere, motifs, slotId, { mode = "light", isMobile = false } = {}) {
  if (!atmosphere) return null;
  const slot = (atmosphere.slots || {})[slotId];
  if (!slot || !slot.enabled) return null;
  if (mode === "dark" ? slot.dark === false : slot.light === false) return null;
  if (isMobile ? slot.mobile === false : slot.desktop === false) return null;
  const list = (motifs || []).filter((m) => m.status !== "archived" && m.url);
  let pick = null;
  if (slot.motif_id) pick = list.find((m) => m.id === slot.motif_id);
  if (!pick && slot.pack) {
    const pool = list.filter((m) => (m.packs || []).includes(slot.pack));
    pick = pool[0] || null;
  }
  if (!pick) return null;
  const pos = POS[slot.position] || POS["top-right"];
  return { url: pick.url, slot, style: pos };
}

export function applyAtmosphere(atmosphere, { reducedMotion = false } = {}) {
  const root = document.documentElement;
  root.dataset.atmStyle = atmosphere?.glow?.style || "none";
  root.classList.toggle("atm-animate", !reducedMotion && (atmosphere?.glow?.style || "none") !== "none");
}

const LS = {
  config: "nirvana-appearance-config",
  mode: "nirvana-theme",
  motion: "nirvana-reduced-motion",
  overrides: "nirvana-overrides",
  atmosphere: "nirvana-atmosphere",
  atmospherePersonal: "nirvana-atmosphere-personal",
};

export function cacheAppearance({ config, mode, reducedMotion, overrides, atmosphere, atmospherePersonal }) {
  try {
    if (config) localStorage.setItem(LS.config, JSON.stringify(config));
    if (mode) localStorage.setItem(LS.mode, mode);
    if (reducedMotion !== undefined) localStorage.setItem(LS.motion, reducedMotion ? "1" : "0");
    if (overrides) localStorage.setItem(LS.overrides, JSON.stringify(overrides));
    if (atmosphere) localStorage.setItem(LS.atmosphere, JSON.stringify(atmosphere));
    if (atmospherePersonal) localStorage.setItem(LS.atmospherePersonal, atmospherePersonal);
  } catch { /* ignore */ }
}
export function readCachedAppearance() {
  let config = FALLBACK_CONFIG, overrides = {}, atmosphere = null;
  try { config = JSON.parse(localStorage.getItem(LS.config)) || FALLBACK_CONFIG; } catch { /* ignore */ }
  try { overrides = JSON.parse(localStorage.getItem(LS.overrides)) || {}; } catch { /* ignore */ }
  try { atmosphere = JSON.parse(localStorage.getItem(LS.atmosphere)) || null; } catch { /* ignore */ }
  return {
    config,
    overrides,
    atmosphere,
    atmospherePersonal: localStorage.getItem(LS.atmospherePersonal) || "follow",
    mode: localStorage.getItem(LS.mode) || "system",
    reducedMotion: localStorage.getItem(LS.motion) === "1",
  };
}

// Build an inline style object (scoped CSS variables) for a given mode of a
// config. Used to preview a draft theme without touching global styles.
export function configToStyle(config, mode = "light") {
  const map = (mode === "dark" ? config?.dark : config?.light) || {};
  const style = {};
  TOKEN_KEYS.forEach((k) => { if (map[k]) style[`--${k}`] = map[k]; });
  style["--radius"] = config?.radius || "0.5rem";
  const g = config?.gradients || {};
  GRADIENT_KEYS.forEach(({ key }) => { style[`--gradient-${key}`] = buildGradientCss(g[key]); });
  style.backgroundColor = `rgb(${map.background || "255 255 255"})`;
  style.color = `rgb(${map.foreground || "0 0 0"})`;
  return style;
}

// ---- contrast engine (AP-4): real WCAG 2.1 AA/AAA validation ----
export function tripletToRgb(t) {
  const p = (t || "").trim().split(/\s+/).map(Number);
  return (p.length >= 3 && !p.some(Number.isNaN)) ? p.slice(0, 3) : [0, 0, 0];
}
function _lin(c) { c /= 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
export function relLuminance(t) {
  const [r, g, b] = tripletToRgb(t);
  return 0.2126 * _lin(r) + 0.7152 * _lin(g) + 0.0722 * _lin(b);
}
export function contrastRatio(t1, t2) {
  const L1 = relLuminance(t1), L2 = relLuminance(t2);
  const hi = Math.max(L1, L2), lo = Math.min(L1, L2);
  return (hi + 0.05) / (lo + 0.05);
}
// WCAG level for normal text (or large text when large=true).
export function aaLevel(ratio, { large = false } = {}) {
  if (large) return ratio >= 4.5 ? "AAA" : ratio >= 3 ? "AA" : "Fail";
  return ratio >= 7 ? "AAA" : ratio >= 4.5 ? "AA" : ratio >= 3 ? "AA Large" : "Fail";
}
// Key foreground/background pairs surfaced in the editor's Contrast tab.
export const CONTRAST_PAIRS = [
  { label: "Body text on background", fg: "foreground", bg: "background" },
  { label: "Card text", fg: "card-foreground", bg: "card" },
  { label: "Muted / helper text", fg: "muted-foreground", bg: "background", large: true },
  { label: "Primary button", fg: "primary-foreground", bg: "primary" },
  { label: "Gold accent chip", fg: "accent-foreground", bg: "accent" },
  { label: "Sage / secondary chip", fg: "secondary-foreground", bg: "secondary" },
  { label: "Error / destructive", fg: "destructive-foreground", bg: "destructive" },
  { label: "Sidebar text", fg: "sidebar-fg", bg: "sidebar-bg" },
];
export function evaluateContrast(tokens) {
  return CONTRAST_PAIRS.map((p) => {
    const ratio = contrastRatio(tokens?.[p.fg], tokens?.[p.bg]);
    return { ...p, ratio, level: aaLevel(ratio, { large: p.large }) };
  });
}
// Quick pass/fail summary for both modes of a config (used on theme cards).
export function contrastSummary(config) {
  const count = (mode) => evaluateContrast((mode === "dark" ? config?.dark : config?.light) || {})
    .filter((r) => r.level === "Fail").length;
  return { lightFails: count("light"), darkFails: count("dark") };
}
// Which colour tokens in a mode differ from a base (org preset) config.
export function diffTokens(config, base, mode) {
  const a = config?.[mode] || {}, b = base?.[mode] || {};
  const changed = new Set();
  TOKEN_KEYS.forEach((k) => { if (b[k] != null && a[k] != null && a[k] !== b[k]) changed.add(k); });
  return changed;
}
